// ============================================================================
// FlashEngine8051 — Erase -> Program -> Verify pipeline for 8051 targets
// ============================================================================
//
// Uses HIDFlashProgrammer interface (not ProbeConnection). 8051 ICP programmers
// handle erase/write/verify at the command level — no SRAM stubs or core
// register access needed.

import type {
  HIDFlashProgrammer,
  ChipDefinition,
  FlashProgress8051,
  FlashResult8051,
} from '@/types/hid8051';

export interface FlashOptions8051 {
  /** Base address for programming (default: 0x0000 for APROM) */
  baseAddr?: number;
  /** Skip erase step */
  skipErase?: boolean;
  /** Skip verify step */
  skipVerify?: boolean;
}

/**
 * FlashEngine8051 orchestrates the full erase -> program -> verify pipeline
 * for 8051 microcontrollers via ICP/ISP programmers.
 */
export class FlashEngine8051 {
  /**
   * Execute the full flash pipeline.
   */
  async flash(
    programmer: HIDFlashProgrammer,
    firmware: Uint8Array,
    chip: ChipDefinition,
    options: FlashOptions8051 = {},
    onProgress?: (p: FlashProgress8051) => void
  ): Promise<FlashResult8051> {
    const baseAddr = options.baseAddr ?? 0x0000;
    const result: FlashResult8051 = {
      success: false,
      stages: {
        erase:   { ok: false, durationMs: 0 },
        program: { ok: false, durationMs: 0, bytesWritten: 0 },
        verify:  { ok: false, durationMs: 0, match: false },
      },
      totalDurationMs: 0,
    };

    // Validate firmware size
    if (firmware.length > chip.flashSize) {
      throw new Error(
        `Firmware size (${firmware.length} bytes) exceeds chip flash size (${chip.flashSize} bytes)`
      );
    }

    const totalStart = performance.now();

    try {
      // Stage 1: Erase
      if (!options.skipErase) {
        onProgress?.({ stage: 'erase', current: 0, total: firmware.length, percent: 0 });
        const eraseStart = performance.now();
        await programmer.massErase();
        result.stages.erase = { ok: true, durationMs: performance.now() - eraseStart };
        onProgress?.({ stage: 'erase', current: firmware.length, total: firmware.length, percent: 100 });
      } else {
        result.stages.erase = { ok: true, durationMs: 0 };
      }

      // Stage 2: Program
      onProgress?.({ stage: 'program', current: 0, total: firmware.length, percent: 0 });
      const programStart = performance.now();
      await programmer.writeFlash(baseAddr, firmware, (p) => {
        if (p.stage === 'program') onProgress?.(p);
      });
      result.stages.program = {
        ok: true,
        durationMs: performance.now() - programStart,
        bytesWritten: firmware.length,
      };

      // Stage 3: Verify
      if (!options.skipVerify) {
        onProgress?.({ stage: 'verify', current: 0, total: firmware.length, percent: 0 });
        const verifyStart = performance.now();
        const match = await programmer.verifyFlash(baseAddr, firmware, (p) => {
          if (p.stage === 'verify') onProgress?.(p);
        });
        result.stages.verify = {
          ok: match,
          durationMs: performance.now() - verifyStart,
          match,
        };
        onProgress?.({ stage: 'verify', current: firmware.length, total: firmware.length, percent: 100 });
      } else {
        result.stages.verify = { ok: true, durationMs: 0, match: true };
      }

      result.success =
        result.stages.erase.ok &&
        result.stages.program.ok &&
        result.stages.verify.ok;
    } catch {
      // Pipeline failure — result.success stays false
    }

    result.totalDurationMs = performance.now() - totalStart;
    return result;
  }
}
